import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getBillingDeploymentReport } from "../../services/Dashboard";
import { getColumnsData } from "./DeploymentsReportColumns";
import { useQuery } from "react-query";
import { isValidDate } from "../../services/Utils";
import SearchIcon from "@mui/icons-material/Search";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import getDataGridToolbarProps from "../common/CustomToolbar";

const DEPLOYMENT_REPORT_STATUS = [
  { label: "Installation Completed", value: "INSTALLATION_COMPLETED" },
  { label: "Download Completed", value: "DOWNLOAD_COMPLETED" },
];

const t = {
  pageBg: "#f3f0f9",
  cardBg: "#ffffff",
  border: "#e0daf0",
  borderHover: "#a89cd4",
  accent: "#6c5ce7",
  accentLight: "#f0edfd",
  accentMid: "#ece8fb",
  labelRed: "#c0415a",
  inputBg: "#faf9fd",
  tableHeaderBg: "#faf9fd",
  tableRowHover: "#faf8ff",
  tableRowBorder: "#f0ecf9",
  textPrimary: "#1a1a2e",
  textSecondary: "#6b6b80",
  textMuted: "#a0a0b0",
  radius: "12px",
  radiusSm: "8px",
  radiusXs: "6px",
};

const cardSx = {
  background: t.cardBg,
  border: `0.5px solid ${t.border}`,
  borderRadius: t.radius,
  boxShadow: "none",
};

const toolbarBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "5px",
  border: `0.5px solid ${t.border}`,
  borderRadius: t.radiusXs,
  color: t.textSecondary,
  fontSize: "12px",
  px: 1.2,
  py: 0.6,
  minWidth: "auto",
  textTransform: "none",
  "&:hover": {
    background: t.accentLight,
    borderColor: t.borderHover,
    color: t.accent,
  },
};

const dataGridSx = {
  border: "none",
  fontFamily: "inherit",
  fontSize: "13px",
  "& .MuiDataGrid-columnHeaders": {
    background: t.tableHeaderBg,
    borderBottom: `0.5px solid ${t.border}`,
    borderRadius: 0,
    minHeight: "42px !important",
  },
  "& .MuiDataGrid-columnHeader": {
    "&:focus, &:focus-within": { outline: "none" },
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontSize: "12px",
    fontWeight: 600,
    color: t.textSecondary,
    letterSpacing: "0.03em",
  },
  "& .MuiDataGrid-sortIcon": { color: t.accent, opacity: 0.6 },
  "& .MuiDataGrid-row": {
    borderBottom: `0.5px solid ${t.tableRowBorder}`,
    "&:hover": { background: t.tableRowHover },
    "&.Mui-selected": {
      background: t.accentMid,
      "&:hover": { background: t.accentLight },
    },
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "none",
    color: t.textPrimary,
    "&:focus, &:focus-within": { outline: "none" },
  },
  "& .MuiCheckbox-root": {
    color: t.border,
    "&.Mui-checked": { color: t.accent },
  },
  "& .MuiDataGrid-footerContainer": {
    borderTop: `0.5px solid ${t.border}`,
    minHeight: "46px",
  },
  "& .MuiTablePagination-root": { fontSize: "12px", color: t.textSecondary },
  "& .MuiTablePagination-select": { fontSize: "12px" },
  "& .MuiIconButton-root": {
    color: t.textSecondary,
    "&:hover": { background: t.accentLight, color: t.accent },
    "&.Mui-disabled": { color: t.border },
  },
  "& .MuiDataGrid-overlay": { background: "transparent" },
};

const DeploymentsReport = (props) => {
  const CustomToolbar = getDataGridToolbarProps("Deployments Report", true);
  const columns = getColumnsData();

  const [status, setStatus] = useState(DEPLOYMENT_REPORT_STATUS[0].value);
  const [picker, setPicker] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1, 0, 0, 0, 0)
  );
  const [pickerInputErrorText, setPickerInputErrorText] = useState(null);
  const [month, setMonth] = useState(picker.getMonth() + 1);
  const [year, setYear] = useState(picker.getFullYear());
  const [rows, setRows] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [fetchData, setFetchData] = useState(true);

  const { isLoading: logsLoading, isFetching } = useQuery(
    [status, month, year],
    getBillingDeploymentReport,
    {
      enabled: fetchData,
      onSuccess: ({ data }) => {
        const tblData = data.map((row, index) => ({ index, ...row }));
        setRows(tblData);
      },
      onSettled: () => setFetchData(false),
      refetchOnWindowFocus: false,
    }
  );

  const handleMonthChange = (val = null) => {
    if (!isValidDate(new Date(val))) {
      setPickerInputErrorText("Invalid Month!");
      return;
    }
    setPickerInputErrorText(null);
    setPicker(val);
  };

  const handleStatusChange = (event) => {
    setStatus(event.target.value);
  };

  const processSearch = () => {
    if (!isValidDate(new Date(picker))) return;
    setMonth(picker.getMonth() + 1);
    setYear(picker.getFullYear());
    setFetchData(true);
  };

  const outlinedFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: t.radiusSm,
      background: t.inputBg,
      fontSize: "13px",
      "& fieldset": { borderColor: t.border, borderWidth: "1px" },
      "&:hover fieldset": { borderColor: t.borderHover },
      "&.Mui-focused fieldset": { borderColor: t.accent, borderWidth: "1.5px" },
    },
    "& .MuiInputLabel-root": {
      fontSize: "13px",
      color: t.textSecondary,
      "&.Mui-focused": { color: t.accent },
    },
    "& .MuiSelect-icon": { color: t.textSecondary },
    "& .MuiFormHelperText-root": { fontSize: "11px" },
  };

  return (
    <Box
      className="fragmentContainer"
      sx={{ p: 3, background: t.pageBg, minHeight: "100vh" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <RocketLaunchOutlinedIcon sx={{ color: t.accent, fontSize: 22 }} />
          <Typography
            sx={{ fontSize: "18px", fontWeight: 500, color: t.textPrimary }}
          >
            Deployments report
          </Typography>
        </Box>
        <Typography sx={{ fontSize: "12px", color: t.textMuted }}>
          Updated just now
        </Typography>
      </Box>

      <Card variant="outlined" sx={{ ...cardSx, mb: 2 }}>
        <CardContent
          sx={{
            py: "14px !important",
            px: "20px !important",
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              color: t.textSecondary,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              mt: "10px",
              whiteSpace: "nowrap",
            }}
          >
            Filters
          </Typography>

          <FormControl size="small" sx={{ minWidth: 220, ...outlinedFieldSx }}>
            <InputLabel>Deployment Status</InputLabel>
            <Select
              value={status}
              onChange={handleStatusChange}
              label="Deployment Status"
            >
              {DEPLOYMENT_REPORT_STATUS.map((item) => (
                <MenuItem
                  key={item.value}
                  value={item.value}
                  sx={{ fontSize: "13px" }}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box
            sx={{
              width: "1px",
              height: "36px",
              background: t.border,
              mt: "4px",
              mx: 0.5,
            }}
          />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <FormControl sx={outlinedFieldSx}>
              <DatePicker
                disableFuture
                label="Month"
                views={["month", "year"]}
                value={picker}
                timezone="UTC"
                onChange={(val) => handleMonthChange(val)}
                maxDate={new Date()}
                minDate={new Date("01-01-2023")}
                slotProps={{
                  textField: {
                    size: "small",
                    variant: "outlined",
                    error: !!pickerInputErrorText,
                    helperText: pickerInputErrorText,
                    onKeyDown: (e) => e.preventDefault(),
                    sx: { minWidth: 148, ...outlinedFieldSx },
                  },
                }}
              />
            </FormControl>
          </LocalizationProvider>

          <Box
            sx={{
              width: "1px",
              height: "36px",
              background: t.border,
              mt: "4px",
              mx: 0.5,
            }}
          />

          <Button
            disabled={!!pickerInputErrorText}
            variant="outlined"
            startIcon={<SearchIcon sx={{ fontSize: "16px !important" }} />}
            onClick={processSearch}
            sx={{
              borderColor: t.accent,
              color: t.accent,
              borderRadius: t.radiusSm,
              textTransform: "none",
              fontSize: "13px",
              fontWeight: 500,
              px: 2,
              py: "7px",
              mt: "2px",
              "&:hover": { background: t.accentLight, borderColor: t.accent },
              "&.Mui-disabled": { borderColor: t.border, color: t.textMuted },
            }}
          >
            Search
          </Button>

          <Typography
            sx={{
              ml: "auto",
              fontSize: "12px",
              color: t.textMuted,
              mt: "10px",
              whiteSpace: "nowrap",
            }}
          >
            {`${DEPLOYMENT_REPORT_STATUS.find((s) => s.value === status)?.label} · ${picker?.toLocaleString("default", { month: "long", year: "numeric" })}`}
          </Typography>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={cardSx}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderBottom: `0.5px solid ${t.border}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 500, color: t.textPrimary }}
            >
              Deployments
            </Typography>
            <Chip
              label={rows?.length ?? 0}
              size="small"
              sx={{
                height: "20px",
                fontSize: "11px",
                fontWeight: 600,
                background: t.accent,
                color: "#fff",
                borderRadius: "20px",
                "& .MuiChip-label": { px: 1 },
              }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Columns">
              <Button
                size="small"
                startIcon={
                  <ViewColumnOutlinedIcon sx={{ fontSize: "15px !important" }} />
                }
                sx={toolbarBtnSx}
              >
                Columns
              </Button>
            </Tooltip>
            <Tooltip title="Filter">
              <Button
                size="small"
                startIcon={
                  <FilterListOutlinedIcon sx={{ fontSize: "15px !important" }} />
                }
                sx={toolbarBtnSx}
              >
                Filter
              </Button>
            </Tooltip>
            <Tooltip title="Export">
              <Button
                size="small"
                startIcon={
                  <FileDownloadOutlinedIcon
                    sx={{ fontSize: "15px !important" }}
                  />
                }
                sx={toolbarBtnSx}
              >
                Export
              </Button>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ height: "calc(100vh - 310px)", width: "100%" }}>
          <DataGrid
            loading={logsLoading || isFetching}
            getRowId={(row) => row?.index}
            rows={rows || []}
            columns={columns}
            checkboxSelection
            disableSelectionOnClick
            experimentalFeatures={{ newEditingApi: true }}
            components={{ Toolbar: CustomToolbar }}
            sx={dataGridSx}
          />
        </Box>
      </Card>
    </Box>
  );
};

export default DeploymentsReport;